import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { Mail, Lock, ArrowRight, User as UserIcon } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { acceptHomeInvitation } from '../lib/household';

interface InviteSignupProps {
  session: any;
}

const InviteSignup = ({ session }: InviteSignupProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';

  const [loading, setLoading] = useState(false);
  const [acceptingExistingSession, setAcceptingExistingSession] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });

  useEffect(() => {
    if (!session?.user || !inviteToken) return;

    const acceptInviteForSession = async () => {
      try {
        setAcceptingExistingSession(true);
        await acceptHomeInvitation(inviteToken);
        navigate('/profile', { replace: true });
      } catch (acceptError: any) {
        setError(acceptError.message || 'Unable to accept invite');
      } finally {
        setAcceptingExistingSession(false);
      }
    };

    acceptInviteForSession();
  }, [session?.user?.id, inviteToken]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleInviteSignup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!inviteToken) {
        throw new Error('Missing invite token. Please open your invite link again.');
      }
      if (!formData.email.trim() || !formData.password.trim()) {
        throw new Error('Email and password are required.');
      }
      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters.');
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email.trim(),
        password: formData.password,
      });
      if (authError) throw authError;

      const user = authData.user;
      if (!user) throw new Error('No user created');

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          full_name: formData.fullName.trim() || null,
          phone_number: null,
          home_manager: 'Brandon Takens',
          terms_accepted_at: new Date().toISOString(),
        });
      if (profileError) throw profileError;

      await acceptHomeInvitation(inviteToken);

      setSuccess('Your account is ready. Redirecting...');
      navigate('/dashboard', { replace: true });
    } catch (signupError: any) {
      setError(signupError.message || 'Unable to create account');
    } finally {
      setLoading(false);
    }
  };

  if (acceptingExistingSession) {
    return (
      <View style={styles.container}>
        <View style={styles.formContainer}>
          <ActivityIndicator size="large" color="#111827" />
          <Text style={styles.infoText}>Joining shared home...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.formContainer}>
        <View style={styles.header}>
          <Text style={styles.title}>Join Shared Home</Text>
          <Text style={styles.subtitle}>
            You were invited to join a household on HoneyDone. Create your account to continue.
          </Text>
        </View>

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Full Name (Optional)</Text>
            <View style={styles.inputWrapper}>
              <View style={styles.iconContainer}>
                <UserIcon size={20} color="#9ca3af" />
              </View>
              <TextInput
                style={styles.input}
                value={formData.fullName}
                onChangeText={(value) => handleInputChange('fullName', value)}
                placeholder="Your name"
              />
            </View>
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Email</Text>
            <View style={styles.inputWrapper}>
              <View style={styles.iconContainer}>
                <Mail size={20} color="#9ca3af" />
              </View>
              <TextInput
                style={styles.input}
                value={formData.email}
                onChangeText={(value) => handleInputChange('email', value)}
                placeholder="you@example.com"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Password</Text>
            <View style={styles.inputWrapper}>
              <View style={styles.iconContainer}>
                <Lock size={20} color="#9ca3af" />
              </View>
              <TextInput
                style={styles.input}
                value={formData.password}
                onChangeText={(value) => handleInputChange('password', value)}
                placeholder="Create a password"
                secureTextEntry
                autoCapitalize="none"
              />
            </View>
          </View>

          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          {success ? <Text style={styles.successText}>{success}</Text> : null}

          <Pressable
            onPress={handleInviteSignup}
            disabled={loading}
            style={({ pressed }) => [
              styles.submitButton,
              (loading || pressed) && styles.submitButtonDisabled,
            ]}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <View style={styles.buttonContent}>
                <Text style={styles.buttonText}>Create account</Text>
                <ArrowRight size={16} color="#ffffff" />
              </View>
            )}
          </Pressable>

          <Pressable onPress={() => navigate(`/login?invite=${inviteToken}`)}>
            <Text style={styles.loginLink}>Already have an account? Sign in</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' ? { minHeight: 'var(--app-height)' as any } : { minHeight: '100%' }),
    backgroundColor: '#f9fafb',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  formContainer: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 24,
  },
  header: {
    gap: 8,
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#111827',
  },
  subtitle: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  form: {
    gap: 14,
  },
  inputContainer: {
    gap: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  inputWrapper: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    position: 'absolute',
    left: 12,
    zIndex: 1,
  },
  input: {
    flex: 1,
    paddingLeft: 40,
    paddingRight: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 16,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  submitButton: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#111827',
    marginTop: 6,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  successText: {
    color: '#16a34a',
    fontSize: 14,
  },
  infoText: {
    fontSize: 14,
    color: '#4b5563',
    marginTop: 10,
  },
  loginLink: {
    fontSize: 14,
    color: '#374151',
    textAlign: 'center',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
});

export default InviteSignup;
